/**
 * Movimentar pneu — instalar, retirar, rodízio, transferir, estoque, recapagem,
 * descarte. As opções vêm da API (`allowedActions`, pela situação atual do
 * pneu); o KM é SUGERIDO pela fonte central do veículo e pode ser corrigido
 * (menor que o último conhecido pede motivo, como no checklist).
 */
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { TireActionDto, TireDetailDto, TireListDto, TireMapDto } from "@bi/types";
import { api, ApiError } from "../../lib/api";
import { IconX } from "../../components/icons";
import { fmtKm, fmtMm, OriginBadge, TireStatusBadge } from "./tire-ui";

export interface MoveTirePreset {
  tireId?: string | null;
  action?: TireActionDto | null;
  destinationPlate?: string | null;
  destinationPositionId?: string | null;
  workOrderId?: string | null;
}

const ACTION_HELP: Partial<Record<TireActionDto, string>> = {
  install: "Monta o pneu numa posição vazia de um veículo.",
  remove: "Tira o pneu do veículo e manda para o estoque, a recapagem ou o descarte.",
  rotate: "Troca de posição no mesmo veículo. Para posição ocupada, marque a troca.",
  transfer: "Leva o pneu deste veículo para uma posição vazia de outro.",
  to_stock: "Registra que o pneu está no estoque (disponível).",
  to_retread: "Envia o pneu para o reformador e abre o registro da recapagem.",
  from_retread: "Registra o retorno do reformador e encerra a recapagem.",
  write_off: "Baixa definitiva do pneu. Exige motivo.",
};

const normPlate = (s: string) => s.toUpperCase().replace(/[^A-Z0-9]/g, "");

export function MoveTireModal({ preset, onClose, onDone }: {
  preset?: MoveTirePreset;
  onClose: () => void;
  onDone?: () => void;
}) {
  const qc = useQueryClient();
  const [tireId, setTireId] = useState<string | null>(preset?.tireId ?? null);
  const [busca, setBusca] = useState("");
  const [action, setAction] = useState<TireActionDto | "">(preset?.action ?? "");
  const [removeTo, setRemoveTo] = useState<"stock" | "retread" | "written_off">("stock");
  const [destPlate, setDestPlate] = useState(preset?.destinationPlate ?? "");
  const [destPos, setDestPos] = useState(preset?.destinationPositionId ?? "");
  const [swap, setSwap] = useState(false);
  const [km, setKm] = useState("");
  const [kmDest, setKmDest] = useState("");
  const [kmMotivo, setKmMotivo] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [occurredAt, setOccurredAt] = useState("");
  const [supplierQ, setSupplierQ] = useState("");
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [retreadType, setRetreadType] = useState("");
  const [treadPattern, setTreadPattern] = useState("");
  const [cost, setCost] = useState("");
  const [outcome, setOutcome] = useState<"returned" | "rejected" | "condemned">("returned");
  const [erro, setErro] = useState<string | null>(null);

  const lista = useQuery({
    queryKey: ["tires", "search", busca],
    queryFn: () => api.get<TireListDto>(`/fleet/tires?q=${encodeURIComponent(busca)}&pageSize=8`),
    enabled: !tireId && busca.trim().length >= 2,
  });

  const det = useQuery({
    queryKey: ["tires", "detail", tireId],
    queryFn: () => api.get<TireDetailDto>(`/fleet/tires/${tireId}`),
    enabled: !!tireId,
  });
  const d = det.data;
  const acoes = d?.allowedActions ?? [];

  // A ação pré-escolhida só vale se a API a permitir para este pneu.
  useEffect(() => {
    if (d && action && !acoes.some((a) => a.action === action)) setAction("");
  }, [d]); // eslint-disable-line react-hooks/exhaustive-deps

  const origemPlaca = d?.tire.location?.origin === "siag" ? d.tire.location.plate : null;
  const precisaDestino = action === "install" || action === "transfer" || action === "rotate";
  const placaDestino = action === "rotate" ? origemPlaca ?? "" : normPlate(destPlate);

  const mapaOrigem = useQuery({
    queryKey: ["tires", "map", origemPlaca],
    queryFn: () => api.get<TireMapDto>(`/fleet/vehicles/${encodeURIComponent(origemPlaca!)}/tire-map`),
    enabled: !!origemPlaca,
  });
  const mapaDestino = useQuery({
    queryKey: ["tires", "map", placaDestino],
    queryFn: () => api.get<TireMapDto>(`/fleet/vehicles/${encodeURIComponent(placaDestino)}/tire-map`),
    enabled: precisaDestino && placaDestino.length >= 7,
    retry: false,
  });
  const veiculos = useQuery({
    queryKey: ["maintenance", "vehicles", destPlate],
    queryFn: () => api.get<{ items: Array<{ plate: string; vehicleTypeName: string | null }> }>(
      `/maintenance/vehicles?q=${encodeURIComponent(destPlate)}`,
    ),
    enabled: (action === "install" || action === "transfer") && destPlate.length >= 2,
  });
  const fornecedores = useQuery({
    queryKey: ["tires", "suppliers", supplierQ],
    queryFn: () => api.get<{ items: Array<{ id: string; name: string; document: string | null }> }>(`/fleet/suppliers?q=${encodeURIComponent(supplierQ)}`),
    enabled: supplierQ.length >= 2 && !supplierId,
  });

  const posicoes = useMemo(
    () => (mapaDestino.data?.axles ?? []).flatMap((a) => a.positions.map((p) => ({ ...p, axleName: a.name }))),
    [mapaDestino.data],
  );
  const posEscolhida = posicoes.find((p) => p.id === destPos);
  const ocupada = !!posEscolhida?.installation && posEscolhida.tire?.id !== tireId;

  // KM sugerido: do veículo de onde o pneu sai; na instalação, do veículo de destino.
  const saiDeVeiculo = !!origemPlaca && action !== "install" && action !== "";
  const sugestaoOrigem = mapaOrigem.data?.odometer ?? null;
  const sugestaoDestino = mapaDestino.data?.odometer ?? null;
  const kmPrincipalSugestao = action === "install" ? sugestaoDestino : saiDeVeiculo ? sugestaoOrigem : null;
  const kmPrincipalTrailer = action === "install" ? mapaDestino.data?.vehicle.isTrailer : mapaOrigem.data?.vehicle.isTrailer;
  const pedeKm = (action === "install" && !!mapaDestino.data) || saiDeVeiculo;

  useEffect(() => {
    if (kmPrincipalSugestao && km === "") setKm(String(kmPrincipalSugestao.km));
  }, [kmPrincipalSugestao]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (action === "transfer" && sugestaoDestino && kmDest === "") setKmDest(String(sugestaoDestino.km));
  }, [sugestaoDestino, action]); // eslint-disable-line react-hooks/exhaustive-deps

  const kmNum = km === "" ? null : Number(km);
  const kmDestNum = kmDest === "" ? null : Number(kmDest);
  const kmAbaixo = (kmNum != null && kmPrincipalSugestao && kmNum < kmPrincipalSugestao.km)
    || (action === "transfer" && kmDestNum != null && sugestaoDestino && kmDestNum < sugestaoDestino.km);
  const enviaRecap = action === "to_retread" || (action === "remove" && removeTo === "retread");
  const descarte = action === "write_off" || (action === "remove" && removeTo === "written_off");

  const mover = useMutation({
    mutationFn: () => api.post(`/fleet/tires/${tireId}/movements`, {
      action,
      removeTo: action === "remove" ? removeTo : null,
      destinationPlate: action === "install" || action === "transfer" ? placaDestino : null,
      destinationPositionId: precisaDestino ? destPos || null : null,
      swap: action === "rotate" ? swap : undefined,
      odometerKm: pedeKm ? kmNum : null,
      destinationOdometerKm: action === "transfer" ? kmDestNum : null,
      odometerOverrideReason: kmMotivo.trim() || null,
      odometerSource: kmPrincipalSugestao && kmNum === kmPrincipalSugestao.km ? kmPrincipalSugestao.source : "informado",
      reason: reason.trim() || null,
      notes: notes.trim() || null,
      occurredAt: occurredAt ? new Date(occurredAt).toISOString() : null,
      workOrderId: preset?.workOrderId ?? null,
      retread: enviaRecap
        ? { supplierId, supplierName: supplierId ? null : supplierQ.trim() || null, retreadType: retreadType.trim() || null, treadPattern: treadPattern.trim() || null }
        : action === "from_retread"
          ? { outcome, cost: cost ? Number(cost.replace(",", ".")) : null, treadPattern: treadPattern.trim() || null }
          : null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tires"] });
      qc.invalidateQueries({ queryKey: ["maintenance"] });
      onDone?.();
      onClose();
    },
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível registrar a movimentação."),
  });

  const pronto = !!tireId && !!action
    && (!precisaDestino || (!!destPos && (!ocupada || (action === "rotate" && swap))))
    && (!descarte || reason.trim().length >= 3)
    && (!pedeKm || kmPrincipalTrailer || kmNum != null)
    && (!kmAbaixo || kmMotivo.trim().length >= 3);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-gray-900/40 p-0 sm:p-4" role="dialog" aria-modal="true" aria-label="Movimentar pneu">
      <div className="absolute inset-0" onClick={onClose} />
      <div className="relative w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto bg-white rounded-t-2xl sm:rounded-xl shadow-xl">
        <div className="sticky top-0 z-10 flex items-center justify-between bg-white px-5 py-4 border-b border-surface-border">
          <h3 className="text-base font-semibold text-gray-900">Movimentar pneu</h3>
          <button className="btn-ghost p-1" onClick={onClose} aria-label="Fechar"><IconX className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Pneu */}
          {!tireId ? (
            <label className="block">
              <span className="text-xs text-gray-500">Pneu (nº de fogo, código ou marca)</span>
              <input className="input w-full mt-0.5" autoFocus value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Ex.: 3BXK221" />
              {(lista.data?.rows ?? []).length > 0 && (
                <ul className="mt-1 border border-surface-border rounded-lg divide-y divide-gray-100 max-h-56 overflow-y-auto">
                  {lista.data!.rows.map((t) => (
                    <li key={t.id}>
                      <button type="button" className="w-full text-left px-3 py-2 hover:bg-surface-muted flex items-center gap-2" onClick={() => setTireId(t.id)}>
                        <span className="font-mono font-medium">{t.code}</span>
                        <span className="text-sm text-gray-500 truncate">{t.brand ?? ""} {t.size ?? ""}</span>
                        <span className="ml-auto"><TireStatusBadge status={t.status} /></span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </label>
          ) : det.isLoading ? (
            <p className="text-sm text-gray-500">Carregando pneu…</p>
          ) : d ? (
            <div className="flex items-start gap-3 rounded-lg bg-surface-muted p-3">
              <div className="flex-1 min-w-0">
                <p className="font-mono font-semibold text-gray-900">Pneu {d.tire.code}</p>
                <p className="text-sm text-gray-500">{[d.tire.brand, d.tire.model, d.tire.size].filter(Boolean).join(" · ")}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {d.tire.location
                    ? <>Local atual: <strong>{d.tire.location.plate}</strong>{d.tire.location.positionLabel ? ` · ${d.tire.location.positionLabel}` : ""}</>
                    : "Fora de veículo"}
                  {" · "}{fmtMm(d.tire.treadMm)} · {d.tire.life}
                </p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <TireStatusBadge status={d.tire.status} />
                <OriginBadge origin={d.tire.statusOrigin} />
              </div>
              {!preset?.tireId && <button className="btn-ghost text-xs" onClick={() => { setTireId(null); setAction(""); }}>Trocar</button>}
            </div>
          ) : null}

          {d && !d.managedBySiag && d.tire.location?.origin === "snapshot" && (
            <p className="text-xs text-warning-700 bg-warning-50 rounded-lg px-3 py-2">
              A Finatto diz que este pneu está em {d.tire.location.plate}{d.tire.location.positionLabel ? ` (${d.tire.location.positionLabel})` : ""},
              mas o SIAG ainda não sabe a posição física. Instale-o na posição correta para o SIAG passar a controlar o histórico.
            </p>
          )}

          {/* Movimentação */}
          {d && (
            acoes.length === 0 ? (
              <p className="text-sm text-gray-500">Este pneu está descartado, não há movimentação possível.</p>
            ) : (
              <label className="block">
                <span className="text-xs text-gray-500">Movimentação *</span>
                <select className="input w-full mt-0.5" value={action} onChange={(e) => { setAction(e.target.value as TireActionDto); setErro(null); }}>
                  <option value="">Escolha…</option>
                  {acoes.map((a) => <option key={a.action} value={a.action}>{a.label}</option>)}
                </select>
                {action && <span className="block text-xs text-gray-400 mt-1">{ACTION_HELP[action]}</span>}
              </label>
            )
          )}

          {action === "remove" && (
            <label className="block">
              <span className="text-xs text-gray-500">Para onde vai</span>
              <select className="input w-full mt-0.5" value={removeTo} onChange={(e) => setRemoveTo(e.target.value as typeof removeTo)}>
                <option value="stock">Estoque</option>
                <option value="retread">Recapagem</option>
                <option value="written_off">Descarte</option>
              </select>
            </label>
          )}

          {/* Destino */}
          {(action === "install" || action === "transfer") && (
            <label className="block">
              <span className="text-xs text-gray-500">Veículo de destino *</span>
              <input className="input w-full mt-0.5 uppercase font-mono" list="mov-placas" value={destPlate}
                onChange={(e) => { setDestPlate(normPlate(e.target.value)); setDestPos(""); }} placeholder="ABC1D23" />
              <datalist id="mov-placas">
                {(veiculos.data?.items ?? []).map((v) => <option key={v.plate} value={v.plate}>{v.vehicleTypeName ?? ""}</option>)}
              </datalist>
            </label>
          )}
          {precisaDestino && placaDestino.length >= 7 && (
            mapaDestino.isLoading ? <p className="text-sm text-gray-500">Carregando posições de {placaDestino}…</p>
              : mapaDestino.isError ? <p className="text-sm text-danger-600">Veículo {placaDestino} não encontrado.</p>
                : !mapaDestino.data?.configuration ? (
                  <p className="text-sm text-warning-700 bg-warning-50 rounded-lg px-3 py-2">
                    {placaDestino} ainda não tem configuração de eixos. Defina-a no Raio-X do veículo (aba Pneus) antes de instalar.
                  </p>
                ) : (
                  <label className="block">
                    <span className="text-xs text-gray-500">Posição de destino *</span>
                    <select className="input w-full mt-0.5" value={destPos} onChange={(e) => { setDestPos(e.target.value); setSwap(false); }}>
                      <option value="">Escolha…</option>
                      {posicoes.filter((p) => p.tire?.id !== tireId).map((p) => (
                        <option key={p.id} value={p.id} disabled={!!p.installation && action !== "rotate"}>
                          {p.label}{p.installation ? ` (ocupada por ${p.tire?.code ?? "?"})` : ""}
                        </option>
                      ))}
                    </select>
                    {action === "rotate" && ocupada && (
                      <label className="mt-2 inline-flex items-center gap-2 text-sm text-gray-700">
                        <input type="checkbox" checked={swap} onChange={(e) => setSwap(e.target.checked)} />
                        Trocar com o pneu {posEscolhida?.tire?.code} (ele vai para a posição atual deste)
                      </label>
                    )}
                  </label>
                )
          )}

          {/* KM */}
          {pedeKm && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs text-gray-500">
                  KM {action === "transfer" ? `de ${origemPlaca}` : action === "install" ? `de ${placaDestino}` : "atual"}
                  {kmPrincipalTrailer ? " (carreta: opcional)" : " *"}
                </span>
                <input className="input w-full mt-0.5" inputMode="numeric" value={km} onChange={(e) => setKm(e.target.value.replace(/\D/g, ""))} />
                {kmPrincipalSugestao && (
                  <span className={`block text-xs mt-1 ${kmPrincipalSugestao.stale ? "text-warning-700" : "text-gray-400"}`}>
                    Sugerido: {fmtKm(kmPrincipalSugestao.km)} · {kmPrincipalSugestao.sourceLabel}
                    {kmPrincipalSugestao.stale ? " (leitura antiga, confira)" : ""}
                  </span>
                )}
              </label>
              {action === "transfer" && (
                <label className="block">
                  <span className="text-xs text-gray-500">KM de {placaDestino || "destino"}{mapaDestino.data?.vehicle.isTrailer ? " (carreta: opcional)" : " *"}</span>
                  <input className="input w-full mt-0.5" inputMode="numeric" value={kmDest} onChange={(e) => setKmDest(e.target.value.replace(/\D/g, ""))} />
                  {sugestaoDestino && <span className="block text-xs mt-1 text-gray-400">Sugerido: {fmtKm(sugestaoDestino.km)} · {sugestaoDestino.sourceLabel}</span>}
                </label>
              )}
              {kmAbaixo && (
                <label className="block sm:col-span-2">
                  <span className="text-xs text-warning-700">O KM informado é menor que o último conhecido. Por quê? *</span>
                  <input className="input w-full mt-0.5" value={kmMotivo} onChange={(e) => setKmMotivo(e.target.value)} placeholder="Ex.: painel trocado" />
                </label>
              )}
            </div>
          )}

          {/* Recapagem */}
          {enviaRecap && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 rounded-lg border border-surface-border p-3">
              <label className="block sm:col-span-3">
                <span className="text-xs text-gray-500">Reformador</span>
                <input className="input w-full mt-0.5" value={supplierQ} onChange={(e) => { setSupplierQ(e.target.value); setSupplierId(null); }} placeholder="Busque no cadastro de fornecedores ou digite o nome" />
                {!supplierId && (fornecedores.data?.items ?? []).length > 0 && (
                  <ul className="mt-1 border border-surface-border rounded-lg divide-y divide-gray-100 max-h-40 overflow-y-auto">
                    {fornecedores.data!.items.map((f) => (
                      <li key={f.id}><button type="button" className="w-full text-left px-3 py-1.5 text-sm hover:bg-surface-muted" onClick={() => { setSupplierId(f.id); setSupplierQ(f.name); }}>{f.name}</button></li>
                    ))}
                  </ul>
                )}
                {supplierId && <span className="text-xs text-tm-700">Fornecedor do cadastro selecionado.</span>}
              </label>
              <label className="block"><span className="text-xs text-gray-500">Tipo</span>
                <input className="input w-full mt-0.5" value={retreadType} onChange={(e) => setRetreadType(e.target.value)} placeholder="Ex.: a frio" /></label>
              <label className="block sm:col-span-2"><span className="text-xs text-gray-500">Banda / desenho</span>
                <input className="input w-full mt-0.5" value={treadPattern} onChange={(e) => setTreadPattern(e.target.value)} /></label>
            </div>
          )}
          {action === "from_retread" && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 rounded-lg border border-surface-border p-3">
              <label className="block"><span className="text-xs text-gray-500">Resultado</span>
                <select className="input w-full mt-0.5" value={outcome} onChange={(e) => setOutcome(e.target.value as typeof outcome)}>
                  <option value="returned">Recapado (volta ao estoque)</option>
                  <option value="rejected">Recusado (volta sem recapar)</option>
                  <option value="condemned">Condenado (descarte)</option>
                </select></label>
              <label className="block"><span className="text-xs text-gray-500">Custo (R$)</span>
                <input className="input w-full mt-0.5" inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value.replace(/[^\d,.]/g, ""))} /></label>
              <label className="block"><span className="text-xs text-gray-500">Banda aplicada</span>
                <input className="input w-full mt-0.5" value={treadPattern} onChange={(e) => setTreadPattern(e.target.value)} /></label>
            </div>
          )}

          {action && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs text-gray-500">Motivo{descarte ? " *" : ""}</span>
                <input className="input w-full mt-0.5" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)}
                  placeholder={descarte ? "Ex.: carcaça condenada" : "Ex.: desgaste irregular, rodízio preventivo"} />
              </label>
              <label className="block">
                <span className="text-xs text-gray-500">Data e hora (vazio = agora)</span>
                <input type="datetime-local" className="input w-full mt-0.5" value={occurredAt} onChange={(e) => setOccurredAt(e.target.value)} />
              </label>
              <label className="block sm:col-span-2">
                <span className="text-xs text-gray-500">Observação</span>
                <textarea className="input w-full mt-0.5" rows={2} value={notes} maxLength={2000} onChange={(e) => setNotes(e.target.value)} />
              </label>
              {preset?.workOrderId && <p className="text-xs text-gray-500 sm:col-span-2">Será registrada na ordem de serviço aberta.</p>}
            </div>
          )}

          {erro && <p className="text-sm text-danger-600">{erro}</p>}
        </div>

        <div className="sticky bottom-0 flex justify-end gap-2 bg-white px-5 py-3 border-t border-surface-border">
          <button className="btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn-primary" disabled={!pronto || mover.isPending} onClick={() => { setErro(null); mover.mutate(); }}>
            {mover.isPending ? "Registrando…" : "Confirmar movimentação"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
